import { getColorById } from '../data/mockData';
import {
  DAKKAPEL_DEFAULT_WIDTH_MM,
  DAKKAPEL_DEFAULT_HEIGHT_MM,
  DAKKAPEL_OUTER_FRAME_MM,
  DAKKAPEL_DIVIDER_MM,
} from '../data/dakkapelOptions';

const escapeXml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');

const resolveHex = (colorId, fallback = '#EDE6D6', customId) => {
  const id = colorId === 'custom' ? customId || 'ral-9001' : colorId;
  if (!id) return fallback;
  const color = getColorById(id);
  return color?.hex || fallback;
};

const shadeHex = (hex, amount) => {
  const raw = String(hex || '#EDE6D6').replace('#', '');
  if (raw.length !== 6) return hex;
  const n = parseInt(raw, 16);
  const clamp = (v) => Math.min(255, Math.max(0, v));
  const r = clamp(((n >> 16) & 255) + amount);
  const g = clamp(((n >> 8) & 255) + amount);
  const b = clamp((n & 255) + amount);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

const round = (value) => Math.round(Number(value) * 100) / 100;

/** SVG ids are document-global; keep them unique so multiple previews can coexist. */
let svgIdCounter = 0;
const nextSvgId = () => {
  svgIdCounter += 1;
  return `dk${svgIdCounter}${Math.random().toString(36).slice(2, 6)}`;
};

/**
 * The elevation is drawn to true scale like the rest of the drawing boards, so a
 * 7000×2000 unit really reads seven metres by two and its bays stay window shaped
 * instead of collapsing into slivers. Clamps only guard absurd inputs.
 */
const displayAspect = (widthMm, heightMm) =>
  Math.min(5, Math.max(0.4, widthMm / Math.max(heightMm, 1)));

// Physical member sizes — keep in sync with dakkapelOptions (x = outer, o = divider).
const OVERHANG_MM = 145; // roof/fascia projection past each cheek
const CHEEK_MM = DAKKAPEL_OUTER_FRAME_MM;
const FRAME_MM = DAKKAPEL_DIVIDER_MM;
const MULLION_MM = DAKKAPEL_DIVIDER_MM;
/** Cladding board pitch. Fixed in mm so courses line up across cheeks and panels. */
const CLADDING_BOARD_MM = 170;
/**
 * Cheek-to-frame seam. Cladding and frame are usually the same colour, so a full
 * strength seam here draws a hard border around the whole glazing area that the
 * elevation should not have — it only needs to hint at the change of plane.
 */
const CHEEK_SEAM_OPACITY = 0.26;
/**
 * Sash section of an opening part (frame plus its bead). A vast raam has no sash at
 * all, so this band is the whole visual difference between the part types.
 *
 * Tied to the divider: a sash reads correctly at about half a divider, so two sashes
 * meeting over a divider come to roughly twice the divider on their own. Any wider and
 * the sash starts to compete with the frame it sits in.
 * SASH_GROOVE_MM is the shadow gap that sets a member back into the one around it.
 */
const SASH_FRAME_MM = DAKKAPEL_DIVIDER_MM / 2;
const SASH_FRAME_MM_RECESSED = DAKKAPEL_DIVIDER_MM * 0.7;
const SASH_GROOVE_MM = 3;

const mmToPx = (mm, pxPerMm) => Math.max(1, mm * pxPerMm);

/** Width-first preview fill — drops slightly when many parts so bays stay readable. */
const getInteractiveWidthFill = (elementCount) => {
  if (elementCount >= 12) return 0.9;
  if (elementCount >= 8) return 0.93;
  if (elementCount >= 6) return 0.95;
  return 0.96;
};

/** Configurator: fill the panel width/height; mm-accurate members prevent squeezed frames. */
const computeInteractiveScale = (canvasW, canvasH, widthMm, heightMm, elementCount) => {
  const maxBoxH = canvasH * 0.82;
  const aspect = displayAspect(widthMm, heightMm);
  const maxTotalW = canvasW * getInteractiveWidthFill(elementCount);

  let boxW = maxTotalW / (1 + (OVERHANG_MM / widthMm) * 2);
  let boxH = boxW / aspect;
  if (boxH > maxBoxH) {
    boxH = maxBoxH;
    boxW = boxH * aspect;
  }

  const pxPerMm = boxW / Math.max(widthMm, 1);
  return { pxPerMm, boxW, boxH };
};

/**
 * The whole elevation sits in ambient daylight, so painted surfaces read a touch
 * darker and warmer than their flat RAL swatch.
 */
const ambient = (hex) => {
  const raw = String(hex || '#EDE6D6').replace('#', '');
  if (raw.length !== 6) return hex;
  const n = parseInt(raw, 16);
  const r = Math.round(((n >> 16) & 255) * 0.97);
  const g = Math.round(((n >> 8) & 255) * 0.93);
  const b = Math.round((n & 255) * 0.925);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

/** Board direction for the cladding seams; sheet materials stay smooth. */
const claddingPattern = (material) => {
  const id = String(material || '');
  if (id.includes('vertical')) return 'vertical';
  if (id.includes('horizontal')) return 'horizontal';
  if (id === 'red-cedar') return 'horizontal';
  return 'none';
};

/**
 * Front elevation of a dakkapel: roof flashing, fascia overhang, clad cheeks,
 * recessed framed glass (inset depth + sash bevels), bottom rail and cast shadow.
 */
export const buildDakkapelSvg = (config = {}, options = {}) => {
  const widthMm = Number(config.width) || DAKKAPEL_DEFAULT_WIDTH_MM;
  const heightMm = Number(config.height) || DAKKAPEL_DEFAULT_HEIGHT_MM;
  const elements =
    Array.isArray(config.elements) && config.elements.length > 0
      ? config.elements
      : [{ id: 'el-1', type: 'fixed-window', widthMm }];

  const claddingRaw = resolveHex(config.claddingColor, '#EDE6D6', config.claddingColorCustom);
  const frameRaw = resolveHex(config.frameColor, '#EDE6D6', config.frameColorCustom);
  const claddingHex = ambient(claddingRaw);
  const frameHex = ambient(frameRaw);
  const fasciaHex = ambient(resolveHex(config.fasciaColor, claddingRaw, config.fasciaColorCustom));
  const tiltHex = ambient(resolveHex(config.tiltTurnColor, frameRaw, config.tiltTurnColorCustom));
  const roofCapHex =
    config.roofFinish === 'colored'
      ? resolveHex(config.roofFinishColor, '#C8CCD0')
      : config.roofFinish === 'zinc-bead'
        ? '#A8ADB3'
        : '#CDD2D6';

  const claddingSeam = shadeHex(claddingHex, -70);
  const frameEdge = shadeHex(frameHex, -70);
  const fasciaEdge = shadeHex(fasciaHex, -34);
  const capEdge = shadeHex(roofCapHex, -28);
  // Flat tone steps stand in for gradients so faces read as boards, not curves.
  const fasciaTopEdge = shadeHex(fasciaHex, -28);
  // Underside of the roof overhang: a shallow band that steps from deep shade back to daylight.
  const soffitSteps = [-72, -84, -64, -42].map((amount) => shadeHex(fasciaHex, amount));
  const soffitLine = shadeHex(fasciaHex, -100);
  // The overhang keeps shading the wall for a short run below the soffit.
  const falloffSteps = [-12, -10, -8, -6, -4, -2].map((amount) => shadeHex(claddingHex, amount));

  const viewW = options.viewWidth || 720;
  const viewH = options.viewHeight || 520;
  const showDims = options.showDimensions !== false;
  const fullBleed = options.fullBleed !== false;
  // Opt-in: saved-quote SVGs and PDF exports must stay inert.
  const interactive = options.interactive === true;
  const uid = nextSvgId();

  // ---- Geometry, proportioned from the reference elevation -----------------
  // Gutters reserve room for the dimension rulers so they never sit on the elevation.
  // With multiple elements we need a second (closer) band for the per-part sub-rulers.
  const hasPartRulers = showDims && elements.length > 1;
  const dimGutterRight = showDims ? 104 : 0;
  const dimGutterBottom = showDims ? (hasPartRulers ? 86 : 48) : 0;
  const canvasW = viewW - dimGutterRight;
  const canvasH = viewH - dimGutterBottom;

  const aspect = displayAspect(widthMm, heightMm);
  const maxBoxH = canvasH * 0.82;

  let boxW;
  let boxH;
  let pxPerMm;

  if (interactive) {
    ({ pxPerMm, boxW, boxH } = computeInteractiveScale(
      canvasW,
      canvasH,
      widthMm,
      heightMm,
      elements.length,
    ));
  } else {
    // Saved-quote / PDF: width-first fill.
    const maxTotalW = canvasW * 0.96;
    boxW = maxTotalW / (1 + (OVERHANG_MM / widthMm) * 2);
    boxH = boxW / aspect;
    if (boxH > maxBoxH) {
      boxH = maxBoxH;
      boxW = boxH * aspect;
    }
    pxPerMm = boxW / Math.max(widthMm, 1);
  }

  const boxX = (canvasW - boxW) / 2;
  const boxY = Math.max(8, (canvasH - boxH) / 2 - canvasH * 0.02);

  const roofCapH = Math.max(2, boxH * 0.0215);
  const fasciaH = Math.max(4, boxH * 0.1196);
  const soffitBandH = Math.max(2, boxH * 0.031);
  const overhang = mmToPx(OVERHANG_MM, pxPerMm);
  const cheekW = mmToPx(CHEEK_MM, pxPerMm);
  const frameW = mmToPx(FRAME_MM, pxPerMm);
  const mullionW = mmToPx(MULLION_MM, pxPerMm);
  const headH = Math.max(2, boxH * 0.041);
  const bottomRailH = Math.max(2, boxH * 0.045);
  /**
   * Cladding strip between the fascia and the top of the kozijn. It belongs to the
   * dakkapel front rather than the window, so it takes the cladding colour along with
   * the cheeks and closes the dark border down both sides and across the top.
   */
  const headCladdingH = Math.max(2, boxH * 0.0255);

  const bodyX = boxX;
  const bodyW = boxW;
  const fasciaY = boxY + roofCapH;
  const soffitY = fasciaY + fasciaH;
  const bodyY = soffitY + soffitBandH;
  const bodyH = boxH - roofCapH - fasciaH - soffitBandH;
  const bodyBottom = bodyY + bodyH;
  // The kozijn hangs below the cladding head strip, so frame members start here.
  const kozijnY = bodyY + headCladdingH;
  const kozijnH = Math.max(4, bodyBottom - kozijnY);

  const fasciaX = boxX - overhang;
  const fasciaW = boxW + overhang * 2;
  const capX = fasciaX - Math.max(1, overhang * 0.05);
  const capW = fasciaW + Math.max(1, overhang * 0.05) * 2;

  /** Shaded overhang underside: stepped strips plus angled returns past the cheeks. */
  const soffitMarkup = (() => {
    const stepH = soffitBandH / soffitSteps.length;
    const strips = soffitSteps
      .map(
        (hex, i) =>
          `<rect x="${round(bodyX)}" y="${round(soffitY + stepH * i)}" width="${round(bodyW)}" height="${round(stepH + 0.5)}" fill="${escapeXml(hex)}"/>`,
      )
      .join('\n    ');
    return `${strips}
    <rect x="${round(bodyX)}" y="${round(bodyY - 1.5)}" width="${round(bodyW)}" height="1.5" fill="${escapeXml(soffitLine)}"/>
    <g shape-rendering="auto">
      <path d="M ${round(fasciaX)} ${round(soffitY)} L ${round(bodyX)} ${round(bodyY)} L ${round(bodyX)} ${round(soffitY)} Z" fill="${escapeXml(soffitSteps[1])}"/>
      <path d="M ${round(fasciaX + fasciaW)} ${round(soffitY)} L ${round(bodyX + bodyW)} ${round(bodyY)} L ${round(bodyX + bodyW)} ${round(soffitY)} Z" fill="${escapeXml(soffitSteps[1])}"/>
    </g>`;
  })();

  const innerX = bodyX + cheekW;
  const innerW = bodyW - cheekW * 2;
  const glassTop = kozijnY + headH;
  const glassBottom = bodyBottom - bottomRailH;
  const glassH = Math.max(8, glassBottom - glassTop);

  // Every inter-pane joint uses the same thin 70 mm mullion — no major structural posts.
  const dividerWAfter = () => mullionW;

  // Element widthMm is clear pane width only. Each bay also carries its share of the
  // fixed dividers (jamb on the outsides, half of the thin/major post toward a neighbour).
  let cursor = innerX;
  const bays = elements.map((el, index) => {
    const isLast = index === elements.length - 1;
    const padLeft = index === 0 ? frameW : dividerWAfter(index - 1) / 2;
    const padRight = isLast ? frameW : dividerWAfter(index) / 2;
    const widthMmEl = Math.max(1, Number(el.widthMm) || 1);
    const glassW = Math.max(4, widthMmEl * pxPerMm);
    const w = padLeft + glassW + padRight;
    const x = cursor;
    cursor += w;
    return {
      x,
      w,
      glassX: x + padLeft,
      glassW,
      padLeft,
      padRight,
      index,
      widthMm: widthMmEl,
      isPanel: el.type === 'fixed-panel',
      // Legacy `tilt-turn` maps to left hinge (same as Kozijnen).
      openingType:
        el.type === 'tilt-turn'
          ? 'tilt-turn-left'
          : el.type === 'tilt-turn-left' ||
              el.type === 'tilt-turn-right' ||
              el.type === 'tilt-only'
            ? el.type
            : null,
      isTilt:
        el.type === 'tilt-turn' ||
        el.type === 'tilt-turn-left' ||
        el.type === 'tilt-turn-right' ||
        el.type === 'tilt-only',
      isCasement: el.type === 'fixed-casement',
    };
  });
  if (bays.length > 0) {
    // Absorb px rounding so the last bay lands exactly on the right cheek.
    const last = bays[bays.length - 1];
    const targetRight = innerX + innerW;
    const delta = targetRight - (last.x + last.w);
    last.w += delta;
    last.glassW = Math.max(4, last.w - last.padLeft - last.padRight);
  }

  /**
   * A vast paneel is a closed cladding panel, not a glazed part, so it is not drawn in
   * the kozijn opening like a pane. It takes the full height of the body and the full
   * width of its bay, which lets the boards run straight on from the cheek next to it
   * instead of sitting in a frame of their own.
   */
  const isClad = (index) => index < 0 || index >= bays.length || bays[index].isPanel;
  /** A divider with a closed panel on both sides — no kozijn meets it. */
  const isCladJoint = (bay) => bay.isPanel && isClad(bay.index + 1);

  const panelRects = bays
    .filter((bay) => bay.isPanel)
    .map((bay) => {
      // Against a cheek the panel runs right up to it so the boards join. Everywhere else
      // it stops at the shared divider, which is drawn as a member in its own right.
      const x = bay.index === 0 ? bay.x : bay.x + bay.padLeft;
      const right =
        bay.index === bays.length - 1 ? bay.x + bay.w : bay.x + bay.w - bay.padRight;
      return { x, y: bodyY, w: Math.max(2, right - x), h: bodyH };
    });

  /**
   * Divider between two closed panels. No kozijn reaches it, so it is clad like the
   * panels either side: a flat face in the cladding colour running the full height, with
   * a shadow line down each edge. The board courses stop against it instead of running
   * through, which is what makes it read as a post rather than a drawn line.
   */
  const cladDividerRects = bays
    .slice(0, -1)
    .filter((bay) => isCladJoint(bay))
    .map((bay) => ({
      x: bay.x + bay.w - bay.padRight,
      y: bodyY,
      w: dividerWAfter(bay.index),
      h: bodyH,
    }));

  /**
   * The runs of the opening that are actually glazed — the inner span with every clad
   * block cut out. The sill, the head seam and the recess shading all follow these, so
   * none of them draw across a panel or a clad divider with no kozijn in front of it.
   */
  const kozijnSpans = (() => {
    const spans = [];
    let edge = innerX;
    [...panelRects, ...cladDividerRects]
      .slice()
      .sort((a, b) => a.x - b.x)
      .forEach((r) => {
        if (r.x > edge) spans.push({ x: edge, w: r.x - edge });
        edge = Math.max(edge, r.x + r.w);
      });
    if (edge < innerX + innerW) spans.push({ x: edge, w: innerX + innerW - edge });
    return spans.filter((s) => s.w > 1);
  })();

  const dimLabel = `${Math.round(widthMm)} × ${Math.round(heightMm)} mm`;
  const pattern = claddingPattern(config.material);

  const hLine = (x, y, w, color, opacity = 0.35, thickness = 1) =>
    `<rect x="${round(x)}" y="${round(y)}" width="${round(w)}" height="${round(Math.max(1, thickness))}" fill="${escapeXml(color)}" opacity="${opacity}"/>`;
  const vLine = (x, y, h, color, opacity = 0.35, thickness = 1) =>
    `<rect x="${round(x)}" y="${round(y)}" width="${round(Math.max(1, thickness))}" height="${round(h)}" fill="${escapeXml(color)}" opacity="${opacity}"/>`;

  /**
   * Lapped cladding board seams. Each course ends in a recessed shadow groove with the
   * lit edge of the next board under it, so the boards overlap rather than read as
   * ruled lines. The groove needs real thickness — at one pixel it reads as a scratch.
   */
  const claddingSeams = (x, y, w, h) => {
    if (pattern === 'none') return '';
    const out = [];
    const boardLip = shadeHex(claddingHex, 26);
    const step = Math.max(6, mmToPx(CLADDING_BOARD_MM, pxPerMm));
    const groove = Math.max(1, Math.min(step * 0.12, 4));

    if (pattern === 'horizontal') {
      // Courses are phased off the body top rather than the region being drawn, so a
      // cheek and a panel of different heights still land on the same board lines.
      const offset = Math.ceil((y - bodyY) / step);
      for (let ly = bodyY + offset * step; ly < y + h - 1; ly += step) {
        if (ly <= y) continue;
        const top = Math.max(y, ly - groove);
        if (ly - top >= 1) out.push(hLine(x, top, w, claddingSeam, 0.34, ly - top));
        out.push(hLine(x, ly, w, boardLip, 0.6));
      }
    } else {
      // Phased off the body's left edge for the same reason: a cheek and a panel next to
      // each other have to share one run of boards.
      const offset = Math.ceil((x - bodyX) / step);
      for (let lx = bodyX + offset * step; lx < x + w - 1; lx += step) {
        if (lx <= x) continue;
        const left = Math.max(x, lx - groove);
        if (lx - left >= 1) out.push(vLine(left, y, h, claddingSeam, 0.34, lx - left));
        out.push(vLine(lx, y, h, boardLip, 0.6));
      }
    }
    return out.join('\n    ');
  };

  const claddingRegions = [
    { x: bodyX, y: bodyY, w: cheekW, h: bodyH },
    { x: bodyX + bodyW - cheekW, y: bodyY, w: cheekW, h: bodyH },
    { x: bodyX, y: bodyY, w: bodyW, h: headCladdingH },
    ...panelRects,
    ...cladDividerRects,
  ];

  const cladFill = (r) =>
    `<rect x="${round(r.x)}" y="${round(r.y)}" width="${round(r.w)}" height="${round(r.h)}" fill="${escapeXml(claddingHex)}"/>`;

  const panelFills = [...panelRects, ...cladDividerRects].map(cladFill).join('\n    ');
  const panelSeams = panelRects.map((r) => claddingSeams(r.x, r.y, r.w, r.h)).join('\n    ');
  // The post's own face stays flat; only its two edges are drawn.
  const cladDividerEdges = cladDividerRects
    .map(
      (r) => `${vLine(r.x, r.y, r.h, claddingSeam, 0.55)}
    ${vLine(r.x + r.w - 1, r.y, r.h, claddingSeam, 0.55)}`
    )
    .join('\n    ');

  /**
   * The cheek-to-kozijn seam marks a change of plane. Where a closed panel sits against
   * a cheek there is no such change — the same cladding carries straight on — so the
   * seam on that side would cut the boards in half.
   */
  const cheekSeamMarkup = [
    bays[0] && bays[0].isPanel
      ? ''
      : vLine(bodyX + cheekW - 1, bodyY, bodyH, claddingSeam, CHEEK_SEAM_OPACITY),
    bays[bays.length - 1] && bays[bays.length - 1].isPanel
      ? ''
      : vLine(bodyX + bodyW - cheekW, bodyY, bodyH, claddingSeam, CHEEK_SEAM_OPACITY),
  ]
    .filter(Boolean)
    .join('\n    ');

  /**
   * Overhang shading below the soffit. These steps are tinted from the cladding colour,
   * so they are clipped to the cladding — cheeks, head strip and any closed panel. Left
   * to run across the whole body they repaint the kozijn head in the dakkapel colour,
   * which is wrong whenever the two colours differ. The kozijn gets overhangDropShadow
   * instead, a neutral fade that works against any frame colour.
   */
  const falloffClipId = `${uid}-falloff`;
  const falloffMarkup = (() => {
    const stepH = (bodyH * 0.14) / falloffSteps.length;
    const strips = falloffSteps
      .map(
        (hex, i) =>
          `<rect x="${round(bodyX)}" y="${round(bodyY + stepH * i)}" width="${round(bodyW)}" height="${round(stepH + 0.5)}" fill="${escapeXml(hex)}"/>`
      )
      .join('\n      ');
    const clip = claddingRegions
      .map(
        (r) =>
          `<rect x="${round(r.x)}" y="${round(r.y)}" width="${round(r.w)}" height="${round(r.h)}"/>`
      )
      .join('\n      ');
    return `<defs><clipPath id="${falloffClipId}">
      ${clip}
    </clipPath></defs>
    <g clip-path="url(#${falloffClipId})">
      ${strips}
    </g>`;
  })();

  // ---- Soft diagonal glass reflections (glass stays glossy) ---------------
  const reflections = (gx, gy, gw, gh, clipId) => {
    const lean = gh * 0.5;
    const wide = gw * 0.3;
    const thin = gw * 0.1;
    const startA = gx - gw * 0.08;
    const startB = startA + gw * 0.46;
    const band = (x0, bw) =>
      `M ${round(x0)} ${round(gy + gh)} L ${round(x0 + bw)} ${round(gy + gh)} L ${round(x0 + bw + lean)} ${round(gy)} L ${round(x0 + lean)} ${round(gy)} Z`;
    return `<g clip-path="url(#${clipId})" filter="url(#${uid}-soft)" opacity="0.85" shape-rendering="auto">
      <path d="${band(startA, wide)}" fill="url(#${uid}-sheen)"/>
      <path d="${band(startB, thin)}" fill="url(#${uid}-sheen)"/>
    </g>`;
  };

  // ---- Window bays --------------------------------------------------------
  // The structure group renders with crispEdges, which snaps each edge to the pixel
  // grid on its own. A reveal left as a sub-pixel sliver behind the sash therefore
  // collapses to nothing on whichever side happens to round inward, so reveals are
  // drawn as explicit whole-pixel hairlines over the shape they outline.
  const revealHex = '#8A8E94';
  const revealW = 1;
  const snap = (value) => Math.round(Number(value));
  const snapW = (x, w) => Math.max(1, snap(x + w) - snap(x));
  const snapH = (y, h) => Math.max(1, snap(y + h) - snap(y));

  const revealFrame = (x, y, w, h) => {
    const x0 = snap(x);
    const y0 = snap(y);
    const x1 = Math.max(x0 + revealW * 2, snap(x + w));
    const y1 = Math.max(y0 + revealW * 2, snap(y + h));
    return `<rect x="${x0}" y="${y0}" width="${x1 - x0}" height="${revealW}" fill="${revealHex}"/>
      <rect x="${x0}" y="${y1 - revealW}" width="${x1 - x0}" height="${revealW}" fill="${revealHex}"/>
      <rect x="${x0}" y="${y0}" width="${revealW}" height="${y1 - y0}" fill="${revealHex}"/>
      <rect x="${x1 - revealW}" y="${y0}" width="${revealW}" height="${y1 - y0}" fill="${revealHex}"/>`;
  };

  /**
   * Directional bevel on a member edge (CSS border-highlight equivalent). Light on
   * top + left reads as a raised face; `inverted` puts the dark side on top + left,
   * which is what makes an opening read as sunk into the face around it.
   * Kept deliberately weak — a strong highlight turns cream members white and makes
   * the sash look like a different colour from the frame it sits in.
   */
  const bevelFrame = (x, y, w, h, baseHex, { inverted = false, strength = 1 } = {}) => {
    const x0 = snap(x);
    const y0 = snap(y);
    const bw = snapW(x, w);
    const bh = snapH(y, h);
    if (bw < 4 || bh < 4) return '';
    const hi = shadeHex(baseHex, 18);
    const lo = shadeHex(baseHex, -34);
    const topLeft = inverted ? lo : hi;
    const bottomRight = inverted ? hi : lo;
    const t = Math.max(1, Math.min(2, Math.floor(Math.min(bw, bh) * 0.03)));
    return `${hLine(x0, y0, bw, topLeft, 0.5 * strength, t)}
      ${vLine(x0, y0, bh, topLeft, 0.4 * strength, t)}
      ${hLine(x0, y0 + bh - t, bw, bottomRight, 0.38 * strength, t)}
      ${vLine(x0 + bw - t, y0, bh, bottomRight, 0.32 * strength, t)}`;
  };

  /** Soft inset around glass — equivalent to box-shadow: inset on the pane edge. */
  const glassInsetShadow = (x, y, w, h) => {
    const x0 = snap(x);
    const y0 = snap(y);
    const bw = snapW(x, w);
    const bh = snapH(y, h);
    if (bw < 6 || bh < 6) return '';
    const t = Math.max(1, Math.min(3, Math.round(Math.min(bw, bh) * 0.02)));
    return `<g opacity="0.14" pointer-events="none">
      <rect x="${x0}" y="${y0}" width="${bw}" height="${t}" fill="#000"/>
      <rect x="${x0}" y="${y0}" width="${t}" height="${bh}" fill="#000"/>
      <rect x="${x0}" y="${y0 + bh - t}" width="${bw}" height="${t}" fill="#000" opacity="0.6"/>
      <rect x="${x0 + bw - t}" y="${y0}" width="${t}" height="${bh}" fill="#000" opacity="0.55"/>
    </g>`;
  };

  // Mullions carry no reveal of their own: the panes either side outline themselves,
  // which laps both faces evenly and can't go asymmetric.
  const frameBar = (x, y, w, h) =>
    `<rect x="${snap(x)}" y="${snap(y)}" width="${Math.max(1, snap(x + w) - snap(x))}" height="${Math.max(1, snap(y + h) - snap(y))}" fill="${escapeXml(frameHex)}"/>`;

  const glassPane = (x, y, w, h, clipId, { withReveal = false } = {}) => {
    const pw = Math.max(3, w);
    const ph = Math.max(3, h);
    return `<defs><clipPath id="${clipId}"><rect x="${round(x)}" y="${round(y)}" width="${round(pw)}" height="${round(ph)}"/></clipPath></defs>
      <rect x="${round(x)}" y="${round(y)}" width="${round(pw)}" height="${round(ph)}" fill="url(#${uid}-glass)"/>
      ${reflections(x, y, pw, ph, clipId)}
      ${glassInsetShadow(x, y, pw, ph)}
      ${withReveal ? revealFrame(x, y, pw, ph) : ''}`;
  };

  const openingFace = (gx, gy, gw, gh, hex) =>
    `<rect x="${snap(gx)}" y="${snap(gy)}" width="${snapW(gx, gw)}" height="${snapH(gy, gh)}" fill="${escapeXml(hex)}"/>`;

  /**
   * Same dashed opening marks as Kozijnen: tilt V from the sill to mid-top,
   * plus turn lines to the hinge side for draai/kiep. Stroke matches DrawingBoard /
   * Kozijnen so the symbol reads identically across configurators.
   * @param {'tilt-turn-left'|'tilt-turn-right'|'tilt-only'} openingType
   */
  const openingLinesForType = (x, y, w, h, openingType) => {
    const x0 = round(x);
    const y0 = round(y);
    const x1 = round(x + w);
    const y1 = round(y + h);
    const mx = round(x + w / 2);
    const my = round(y + h / 2);
    const stroke = 'stroke="#999999" stroke-width="3" stroke-dasharray="3"';
    const tilt = `<line x1="${x0}" y1="${y1}" x2="${mx}" y2="${y0}" ${stroke}/>
      <line x1="${x1}" y1="${y1}" x2="${mx}" y2="${y0}" ${stroke}/>`;
    if (openingType === 'tilt-only') {
      return `<g shape-rendering="auto" pointer-events="none">${tilt}</g>`;
    }
    const turn =
      openingType === 'tilt-turn-right'
        ? `<line x1="${x0}" y1="${y0}" x2="${x1}" y2="${my}" ${stroke}/>
      <line x1="${x0}" y1="${y1}" x2="${x1}" y2="${my}" ${stroke}/>`
        : `<line x1="${x1}" y1="${y0}" x2="${x0}" y2="${my}" ${stroke}/>
      <line x1="${x1}" y1="${y1}" x2="${x0}" y2="${my}" ${stroke}/>`;
    return `<g shape-rendering="auto" pointer-events="none">
      ${tilt}
      ${turn}
    </g>`;
  };

  /**
   * A glazed part. Depth comes from a shadow gap at the opening edge plus an inverted
   * bevel, which sets the glazing back behind the outer frame face.
   *
   * The sash band is what separates the part types, so only opening parts get one:
   * a vast raam is glazed straight into the frame opening (the pane runs out to the
   * jamb and mullion, with just the reveal hairline around it), while a draai/kiep or
   * vaste vleugel carries a sash section drawn in the frame — or draaideel — colour.
   * Giving a vast raam a band of its own is what made it read as an opening part.
   * The band comes from mm, so bays of different widths keep equal margins.
   */
  const glazedPane = (gx, gy, gw, gh, clipId, memberHex, deepFrame, withSash, openingType = null) => {
    // Shadow gap: the outer frame face stops here, everything inside sits behind it.
    const gap = Math.max(1, Math.min(mmToPx(SASH_GROOVE_MM, pxPerMm), gw * 0.02, gh * 0.015, 3));

    const x = gx + gap;
    const y = gy + gap;
    const w = Math.max(4, gw - gap * 2);
    const h = Math.max(4, gh - gap * 2);

    const band = withSash
      ? Math.max(
          2,
          Math.min(
            mmToPx(deepFrame ? SASH_FRAME_MM_RECESSED : SASH_FRAME_MM, pxPerMm),
            w * 0.2,
            h * 0.2
          )
        )
      : 0;

    const glassX = x + band;
    const glassY = y + band;
    const glassW = Math.max(2, w - band * 2);
    const glassHInner = Math.max(2, h - band * 2);

    const sash = band
      ? `${openingFace(x, y, w, h, memberHex)}
      ${bevelFrame(x, y, w, h, memberHex)}`
      : '';

    const openingLines =
      openingType && glassW > 8 && glassHInner > 8
        ? openingLinesForType(glassX, glassY, glassW, glassHInner, openingType)
        : '';

    return `${openingFace(gx, gy, gw, gh, frameHex)}
      ${openingFace(gx, gy, gw, gh, shadeHex(frameHex, -62))}
      ${bevelFrame(gx, gy, gw, gh, frameHex, { inverted: true, strength: 1.25 })}
      ${sash}
      ${glassPane(glassX, glassY, glassW, glassHInner, clipId, {
        withReveal: true,
      })}
      ${openingLines}`;
  };

  const baysMarkup = bays
    .map((bay, i) => {
      const gx = bay.glassX;
      const gw = bay.glassW;
      const clipId = `${uid}-g${i}`;

      // Closed panels are drawn with the cladding, not here.
      if (bay.isPanel) return '';

      const deepFrame = config.frameType === 'recessed';
      // Vast raam glazes straight into the frame rebate; draai/kiep / alleen kiep and
      // vaste vleugel are sash parts, so they get the extra sash section.
      const withSash = bay.isTilt || bay.isCasement;

      return `<g>
      ${glazedPane(gx, glassTop, gw, glassH, clipId, bay.isTilt ? tiltHex : frameHex, deepFrame, withSash, bay.openingType)}
    </g>`;
    })
    .join('\n');

  // A divider is only a kozijn member if there is glass on at least one side of it;
  // otherwise it is clad, and drawn with the cladding as cladDividerRects.
  const mullionsMarkup = bays
    .slice(0, -1)
    .filter((bay) => !isCladJoint(bay))
    .map((bay) => frameBar(bay.x + bay.w - bay.padRight, glassTop, mullionW, glassH))
    .join('\n');

  const majorPostsMarkup = '';

  // Click targets sit over each element's glazing, on top of the bays but clear of the
  // sill so they never swallow clicks meant for the sub-ruler inputs below.
  const hitMarkup = interactive
    ? bays
        .map(
          (bay) =>
            `<rect class="dk-hit" data-dakkapel-element="${bay.index}" x="${round(bay.x)}" y="${round(glassTop)}" width="${round(bay.w)}" height="${round(glassH)}"/>`
        )
        .join('\n    ')
    : '';

  const sunBar =
    config.sunProtection === 'screens' || config.sunProtection === 'shutters'
      ? `<rect x="${round(innerX)}" y="${round(kozijnY + 1)}" width="${round(innerW)}" height="${round(Math.max(6, headH * 0.8))}" fill="#5c6670" opacity="0.5"/>`
      : '';

  /**
   * Outer-frame recess: pushes the whole glass assembly behind the cheeks / head / sill.
   * Cheeks and frame are normally the same colour, so this has to stay a faint gradient
   * of shade — stacking opaque bands here paints a hard border around the glazing, which
   * reads as an extra frame instead of a change of plane.
   */
  const cavityInsetShadow = (() => {
    const y = snap(kozijnY);
    const h = snapH(kozijnY, kozijnH);
    const topBand = Math.max(3, Math.min(10, Math.round(headH * 0.55)));
    const sideBand = Math.max(1, Math.min(4, Math.round(cheekW * 0.04)));
    const bottomBand = Math.max(2, Math.min(7, Math.round(bottomRailH * 0.45)));
    const bands = kozijnSpans
      .map((span) => {
        const x = snap(span.x);
        const w = snapW(span.x, span.w);
        return `<rect x="${x}" y="${y}" width="${w}" height="${topBand}" fill="#000"/>
      <rect x="${x}" y="${y}" width="${sideBand}" height="${h}" fill="#000"/>
      <rect x="${x + w - sideBand}" y="${y}" width="${sideBand}" height="${h}" fill="#000" opacity="0.85"/>
      <rect x="${x}" y="${y + h - bottomBand}" width="${w}" height="${bottomBand}" fill="#000" opacity="0.75"/>`;
      })
      .join('\n      ');
    return `<g class="dk-cavity-inset" pointer-events="none" opacity="0.09">
      ${bands}
    </g>`;
  })();

  /** Soft band cast by the fascia onto the head beam (drop-shadow under the overhang). */
  const overhangDropShadow = (() => {
    const shadowH = Math.max(4, Math.min(14, Math.round(fasciaH * 0.28 + soffitBandH * 0.6)));
    return `<rect class="dk-overhang-shadow" x="${round(bodyX)}" y="${round(bodyY)}" width="${round(bodyW)}" height="${round(shadowH)}" fill="url(#${uid}-overhang-fade)" pointer-events="none"/>`;
  })();

  const background = fullBleed
    ? `<rect x="${round(-viewW)}" y="${round(-viewH)}" width="${round(viewW * 3)}" height="${round(viewH * 3)}" fill="url(#${uid}-sky)"/>`
    : `<rect width="${viewW}" height="${viewH}" fill="#f5f5f5"/>`;

  /** Width/height rulers in the shared drawing-board style, plus per-element sub-rulers. */
  const dimsMarkup = (() => {
    if (!showDims) return '';

    const ink = '#2C2C2C';
    const partInk = '#4A4A4A';
    const tick = 8;
    const partTick = 5;
    const rule = `stroke="${ink}" stroke-width="1.5"`;
    const partRule = `stroke="${partInk}" stroke-width="1"`;
    // Main labels are editable inputs like the Kozijnen board. Kept at 72 wide: over 55
    // so the PDF export treats them as main (not per-part) labels, but narrow enough that
    // the height label still clears the right gutter.
    const mainFoW = 72;
    const mainFoH = 20;
    const numberStyle = (align, color, size) =>
      `width:100%;height:100%;border:none;background:transparent;text-align:${align};font-family:Inter,Segoe UI,system-ui,sans-serif;font-size:${size}px;font-weight:600;color:${color};outline:none;padding:0;margin:0;-webkit-appearance:none;-moz-appearance:textfield;`;
    const unitStyle = `font-family:Inter,Segoe UI,system-ui,sans-serif;font-size:11px;font-weight:600;color:${ink};opacity:0.7;flex-shrink:0;`;
    const mainLabel = (axis, mm, x, y, justify) =>
      `<foreignObject x="${round(x)}" y="${round(y)}" width="${mainFoW}" height="${mainFoH}" data-dim-axis="${axis}">
      <div xmlns="http://www.w3.org/1999/xhtml" style="display:flex;align-items:center;justify-content:${justify};gap:3px;white-space:nowrap;width:100%;height:100%;">
        <input data-dakkapel-dim="${axis}" type="number" step="50" value="${Math.round(mm)}" style="${numberStyle('right', ink, 11)}"/>
        <span style="${unitStyle}">mm</span>
      </div>
    </foreignObject>`;

    // Match DrawingBoard offsets: sub-rulers hug the sill; the main width drops below them.
    const partDimY = bodyBottom + 14;
    const widthY = bodyBottom + (hasPartRulers ? 58 : 40);
    const heightX = fasciaX + fasciaW + 30;

    // Continuous sub-ruler: one line across all panes, ticks at the glass start/end and
    // each mullion centre — same style as the Kozijnen board and the dakkapel.nl preview.
    const partRulers = (() => {
      if (!hasPartRulers || bays.length < 2) return '';
      const y = partDimY;
      const edges = [bays[0].glassX];
      for (let i = 0; i < bays.length - 1; i += 1) {
        edges.push(bays[i].x + bays[i].w);
      }
      const lastBay = bays[bays.length - 1];
      edges.push(lastBay.glassX + lastBay.glassW);

      const ticks = edges
        .map(
          (x) =>
            `<line x1="${round(x)}" y1="${round(y - partTick)}" x2="${round(x)}" y2="${round(y + partTick)}" ${partRule}/>`
        )
        .join('\n    ');

      const labels = bays
        .map((bay, i) => {
          const mid = (edges[i] + edges[i + 1]) / 2;
          const foW = 44;
          return `<foreignObject x="${round(mid - foW / 2)}" y="${round(y + 4)}" width="${foW}" height="18">
      <div xmlns="http://www.w3.org/1999/xhtml" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;">
        <input data-dakkapel-part-width="${bay.index}" type="number" value="${Math.round(bay.widthMm)}" style="width:100%;height:100%;border:none;background:transparent;text-align:center;font-size:10px;font-weight:500;color:${partInk};outline:none;padding:0;margin:0;-webkit-appearance:none;-moz-appearance:textfield;"/>
      </div>
    </foreignObject>`;
        })
        .join('\n    ');

      return `<g>
    <line x1="${round(edges[0])}" y1="${round(y)}" x2="${round(edges[edges.length - 1])}" y2="${round(y)}" ${partRule}/>
    ${ticks}
    ${labels}
  </g>`;
    })();

    return `<g shape-rendering="auto">
    ${partRulers}
    <line x1="${round(bodyX)}" y1="${round(widthY)}" x2="${round(bodyX + bodyW)}" y2="${round(widthY)}" ${rule}/>
    <line x1="${round(bodyX)}" y1="${round(widthY - tick)}" x2="${round(bodyX)}" y2="${round(widthY + tick)}" ${rule}/>
    <line x1="${round(bodyX + bodyW)}" y1="${round(widthY - tick)}" x2="${round(bodyX + bodyW)}" y2="${round(widthY + tick)}" ${rule}/>
    ${mainLabel('width', widthMm, bodyX + bodyW / 2 - mainFoW / 2, widthY + 6, 'center')}

    <line x1="${round(heightX)}" y1="${round(boxY)}" x2="${round(heightX)}" y2="${round(bodyBottom)}" ${rule}/>
    <line x1="${round(heightX - tick)}" y1="${round(boxY)}" x2="${round(heightX + tick)}" y2="${round(boxY)}" ${rule}/>
    <line x1="${round(heightX - tick)}" y1="${round(bodyBottom)}" x2="${round(heightX + tick)}" y2="${round(bodyBottom)}" ${rule}/>
    ${mainLabel('height', heightMm, heightX + 8, (boxY + bodyBottom) / 2 - mainFoH / 2, 'flex-start')}
  </g>`;
  })();

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${viewW} ${viewH}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" role="img" aria-label="Dakkapel ${escapeXml(dimLabel)}">
  <defs>
    <linearGradient id="${uid}-sky" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="${viewH}">
      <stop offset="0%" stop-color="#9BBFDE"/>
      <stop offset="38%" stop-color="#CFE2F1"/>
      <stop offset="62%" stop-color="#FBFCFD"/>
      <stop offset="100%" stop-color="#FFFFFF"/>
    </linearGradient>
    <linearGradient id="${uid}-glass" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#C3DEFA"/>
      <stop offset="55%" stop-color="#B2D5F9"/>
      <stop offset="100%" stop-color="#9DCCF8"/>
    </linearGradient>
    <linearGradient id="${uid}-sheen" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0%" stop-color="#FFFFFF" stop-opacity="0"/>
      <stop offset="45%" stop-color="#FFFFFF" stop-opacity="0.5"/>
      <stop offset="60%" stop-color="#FFFFFF" stop-opacity="0.42"/>
      <stop offset="100%" stop-color="#FFFFFF" stop-opacity="0"/>
    </linearGradient>
    <linearGradient id="${uid}-overhang-fade" gradientUnits="userSpaceOnUse" x1="0" y1="${round(bodyY)}" x2="0" y2="${round(bodyY + Math.max(4, Math.min(14, fasciaH * 0.28 + soffitBandH * 0.6)))}">
      <stop offset="0%" stop-color="#000000" stop-opacity="0.22"/>
      <stop offset="55%" stop-color="#000000" stop-opacity="0.08"/>
      <stop offset="100%" stop-color="#000000" stop-opacity="0"/>
    </linearGradient>
    <filter id="${uid}-soft" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="${round(Math.max(2, innerW * 0.008))}"/>
    </filter>
    <filter id="${uid}-cast" x="-30%" y="-60%" width="160%" height="260%">
      <feGaussianBlur stdDeviation="${round(Math.max(4, bodyW * 0.014))}"/>
    </filter>
    <filter id="${uid}-fascia-drop" x="-5%" y="0%" width="110%" height="160%">
      <feDropShadow dx="0" dy="4" stdDeviation="2.5" flood-color="#000000" flood-opacity="0.22"/>
    </filter>
  </defs>
  ${
    interactive
      ? `<style>
    .dk-hit{fill:transparent;pointer-events:all;cursor:pointer;}
    .dk-hit:hover{fill:rgba(255,165,0,0.15);stroke:rgba(255,140,0,0.65);stroke-width:2;}
    .dk-cavity-inset{mix-blend-mode:multiply;}
    .dk-overhang-shadow{mix-blend-mode:multiply;}
  </style>`
      : `<style>
    .dk-cavity-inset{mix-blend-mode:multiply;}
    .dk-overhang-shadow{mix-blend-mode:multiply;}
  </style>`
  }

  ${background}

  <ellipse cx="${round(bodyX + bodyW / 2)}" cy="${round(bodyBottom + Math.max(5, bodyH * 0.022))}" rx="${round(bodyW * 0.5)}" ry="${round(Math.max(5, bodyH * 0.03))}" fill="#64748b" opacity="0.3" filter="url(#${uid}-cast)"/>

  <g shape-rendering="crispEdges">
    <rect x="${round(bodyX)}" y="${round(bodyY)}" width="${round(bodyW)}" height="${round(bodyH)}" fill="${escapeXml(frameHex)}"/>

    <rect x="${round(bodyX)}" y="${round(bodyY)}" width="${round(cheekW)}" height="${round(bodyH)}" fill="${escapeXml(claddingHex)}"/>
    <rect x="${round(bodyX + bodyW - cheekW)}" y="${round(bodyY)}" width="${round(cheekW)}" height="${round(bodyH)}" fill="${escapeXml(claddingHex)}"/>
    <!-- Cladding above the kozijn, joining the two cheeks into a border across the top. -->
    <rect x="${round(bodyX)}" y="${round(bodyY)}" width="${round(bodyW)}" height="${round(headCladdingH)}" fill="${escapeXml(claddingHex)}"/>
    <!-- Closed panels belong to this cladding layer so they take the same shading. -->
    ${panelFills}

    ${falloffMarkup}

    ${claddingSeams(bodyX, bodyY, cheekW, bodyH)}
    ${claddingSeams(bodyX + bodyW - cheekW, bodyY, cheekW, bodyH)}
    ${panelSeams}
    ${cladDividerEdges}
    ${cheekSeamMarkup}

    ${overhangDropShadow}

    ${sunBar}
    ${baysMarkup}
    ${mullionsMarkup}
    ${cavityInsetShadow}
    ${hitMarkup}

    <!-- Bottom rail only between the cheeks so the outer vertical seams keep running to the sill. -->
    ${kozijnSpans
      .map(
        (span) =>
          `<rect x="${round(span.x)}" y="${round(glassBottom)}" width="${round(span.w)}" height="${round(bottomRailH)}" fill="${escapeXml(frameHex)}"/>`
      )
      .join('\n    ')}
    ${hLine(bodyX, bodyBottom - 1, bodyW, frameEdge, 0.5)}

    <!-- No head or sill seam across the full inner width: each pane already draws its own
         bevel and shadow gap, so a line spanning the opening only shows up where it runs
         over a jamb or the mullion — a stray rule across the corners. -->
    <!-- Shadow line where the cladding head strip meets the top of the kozijn. -->
    ${kozijnSpans.map((span) => hLine(span.x, kozijnY, span.w, claddingSeam, 0.5)).join('\n    ')}
    <!-- Full-height major posts + edge cuts so head and sill read as split at each post. -->
    ${majorPostsMarkup}
    ${vLine(bodyX, bodyY, bodyH, claddingSeam, 0.85)}
    ${vLine(bodyX + bodyW - 1, bodyY, bodyH, claddingSeam, 0.85)}
    <!-- Re-stroke cheek seams on top of the sill so they meet the bottom edge. -->
    ${cheekSeamMarkup}

    ${soffitMarkup}

    <g filter="url(#${uid}-fascia-drop)">
      <rect x="${round(fasciaX)}" y="${round(fasciaY)}" width="${round(fasciaW)}" height="${round(fasciaH)}" fill="${escapeXml(fasciaHex)}"/>
      ${hLine(fasciaX, fasciaY, fasciaW, fasciaTopEdge, 1)}
      ${vLine(fasciaX, fasciaY, fasciaH, fasciaEdge, 0.4)}
      ${vLine(fasciaX + fasciaW - 1, fasciaY, fasciaH, fasciaEdge, 0.4)}
    </g>

    <rect x="${round(capX)}" y="${round(boxY)}" width="${round(capW)}" height="${round(roofCapH)}" fill="${escapeXml(roofCapHex)}"/>
    ${hLine(capX, boxY, capW, shadeHex(roofCapHex, 18), 0.6)}
    ${hLine(capX, boxY + roofCapH - 1, capW, capEdge, 0.6)}
  </g>

  ${dimsMarkup}
</svg>`;
};

export const buildDakkapelSavedSvg = (config) =>
  buildDakkapelSvg(config, {
    viewWidth: 400,
    viewHeight: 240,
    showDimensions: false,
    fullBleed: false,
  });

export const buildDakkapelSavedSvgAsync = async (config) => buildDakkapelSavedSvg(config);
